import { UserClaims } from "@okta/okta-auth-js";
import { useOktaAuth } from "@okta/okta-react";
import { useEffect, useRef } from "react";
import { Outlet, useLocation } from "react-router";
import {
  isAdmin,
  ManagerGroups,
  OrionChatGroups,
  OrionContributorGroups,
  OrionManagerGroups,
  OrionUserGroups,
  SiriusContributorGroups,
  SiriusManagerGroups,
  SiriusUserGroups,
  useSession,
} from "../useSession";

type ReemiaUserClaim = UserClaims & {
  first_name: string;
  last_name: string;
  company_id: string;
  company_alphabetic_name: string;
  permissions: string;
};

export const SecurePage: React.FC = () => {
  const { oktaAuth, authState } = useOktaAuth();
  const { session, setSession } = useSession();
  const location = useLocation();
  const redirectPath = useRef(location.state?.from?.pathname);

  useEffect(() => {
    const handleAuthState = async () => {
      if (!authState) {
        return;
      }
      if (!authState.isAuthenticated) {
        await oktaAuth.signInWithRedirect({
          originalUri: redirectPath.current,
        });
        return;
      }

      try {
        const accessToken = authState.accessToken;
        if (!accessToken) {
          throw new Error("The user is authenticated but has no accessToken");
        }
        const claim = accessToken.claims as ReemiaUserClaim;
        const roles = accessToken.claims.permissions
          ? JSON.parse(accessToken.claims.permissions as string)
          : undefined;
        setSession({
          user: {
            id: claim.sub,
            email: claim.sub,
            firstname: claim.first_name,
            lastname: claim.last_name,
          },
          org: {
            id: claim.company_id,
            name: claim.company_alphabetic_name,
          },
          accessToken: accessToken.accessToken,
          roles: roles,
          OrionUserGroups: OrionUserGroups(roles),
          OrionContributorGroups: OrionContributorGroups(roles),
          OrionManagerGroups: OrionManagerGroups(roles),
          OrionChatGroups: OrionChatGroups(roles),
          SiriusUserGroups: SiriusUserGroups(roles),
          SiriusContributorGroups: SiriusContributorGroups(roles),
          SiriusManagerGroups: SiriusManagerGroups(roles),
          ManagerGroups: ManagerGroups(roles),
          isAdmin: isAdmin(roles),
        });
      } catch (error) {
        console.error("Failed to get permissions from token", error);
      }
    };
    handleAuthState();
  }, [authState, oktaAuth, setSession]);

  if (!authState || !authState?.isAuthenticated) {
    return null;
  }

  return session ? <Outlet /> : null;
};
